/**
 * The root navigator is used to switch between major navigation flows of your app.
 * Generally speaking, it will contain an auth flow (registration, login, forgot password)
 * and a "main" flow (which is contained in your PrimaryNavigator) which the user
 * will use once logged in.
 */
import React, { useState, useEffect } from "react";
import { Platform } from "react-native";
import { NavigationContainer, NavigationContainerRef } from "@react-navigation/native";
import { useSelector } from "react-redux";
import { createStackNavigator } from "@react-navigation/stack";
import { Header } from "../components/new/header/header";
import { MoreParamsList } from "./more-stack/more-stack";
import { theme, ThemeType } from "../theme/theme";
import { HomeParamsList } from "./home-stack/home-stack";
import { useTheme } from "react-native-paper";
import { UnauthedStack } from "./unauthed-stack/unauthed-stack";
import { AuthedStack } from "./authed-stack";
import { MaintenanceStack } from "./maintenance-stack";
import { RootState } from "../redux/reducers";
import { MaintenancePolling } from "../utils/maintenance";
import { ErrorBoundary } from "react-error-boundary";
import { NoNetworkStack } from "./no-network-stack/index";
import { ErrorBoundaryRender } from "../components/error-boundary-render/error-boundary-render";
import { useDispatch } from "react-redux";
import NetInfo from "@react-native-community/netinfo";
import { signOutUser } from "../redux/actions/actionCreators";
import { base64Logo } from "../utils/base64Logo";
import Heap from "@heap/react-native-heap";

export type RootStackParamsList = {
  Home: HomeParamsList;
  More: MoreParamsList;
};

const Stack = createStackNavigator();

const getBrowserNavigationLinking = () => {
  // NOTE: This config is used for browser linking.
  //  https://reactnavigation.org/docs/configuring-links/
  // We need to fill this up to when we decide to move fully to react web.
  // TODO: Add DCash recovery screens names here.
  const config = {
    screens: {
      Unauthed: {
        screens: {
          SignIn: {
            path: "signin",
          },
        },
      },
      Authed: {
        screens: {
          Actions: {
            screens: {
              PaymentFlowPaymentMethodCertifiedACH: {
                path: "Actions/PaymentFlowPaymentMethodCertifiedACH/",
              },
              PaymentFlowPaymentMethodCertifiedACHYodleeWidget: {
                path: "Actions/PaymentFlowPaymentMethodCertifiedACHYodleeWidget/",
              },
            },
          },
        },
      },
    },
  };

  const linking = {
    prefixes: [],
    config,
  };

  return linking;
};

enum AppState {
  Maintenance = "Maintenance",
  Authed = "Authed",
  Unauthed = "Unauthed",
  NoNetworkDetected = "NoNetworkDetected",
}

const RootStack = () => {
  const userInfo = useSelector((state: RootState) => state?.auth?.userInfo);
  const logger = useSelector((state: RootState) => state?.logger);
  const theme = useTheme() as ThemeType;
  const iconSize = theme.iconSize.small;
  const isLoggedIn = Boolean(userInfo.username);
  const appMode = useSelector((state: RootState) => state.reservation.appMode);
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [networkError, setNetworkError] = useState<boolean>(false);
  const [fallbackLogo, setFallbackLogo] = useState<string | ArrayBuffer | null>(null);

  useEffect(() => {
    MaintenancePolling.start(setMaintenanceMode);
    return () => MaintenancePolling.stop();
  });
  useEffect(() => {
    const unsubscribe = NetInfo.addEventListener((state) => {
      if (!state.isConnected) setFallbackLogo(base64Logo);
      setNetworkError(!state.isConnected);
    });
    return () => {
      // console.log("unsubscribing from internet listener");
      unsubscribe();
    };
  }, []);

  let appState: AppState = AppState.Unauthed;
  if (maintenanceMode) {
    appState = AppState.Maintenance;
  } else if (networkError) {
    appState = AppState.NoNetworkDetected;
  } else if (isLoggedIn) {
    appState = AppState.Authed;
  }

  const stateMapping = {
    [AppState.Authed]: AuthedStack,
    [AppState.Unauthed]: UnauthedStack,
    [AppState.Maintenance]: MaintenanceStack,
    [AppState.NoNetworkDetected]: NoNetworkStack,
  };
  return (
    <Stack.Navigator screenOptions={{ omitProfileIcon: false, showFullMenu: false }}>
      <Stack.Screen
        name={appState.toString()}
        component={stateMapping[appState]}
        options={({ route }) => ({
          header: (props) =>
            appState === AppState.NoNetworkDetected ? (
              <Header {...props} networkError={networkError} fallbackLogo={fallbackLogo} />
            ) : null,
          omitBackButton: true,
          omitRightContent: true,
          useLight: true,
        })}
      />
    </Stack.Navigator>
  );
};

export const RootNavigator = React.forwardRef<
  NavigationContainerRef,
  Partial<React.ComponentProps<typeof NavigationContainer>>
>((props, ref) => {
  const linking = getBrowserNavigationLinking();
  const dispatch = useDispatch();

  let navigationContainer;
  if (Platform.OS == "web") {
    navigationContainer = (
      <NavigationContainer {...props} ref={ref} linking={linking}>
        <RootStack />
      </NavigationContainer>
    );
  } else {
    const HeapNavigationContainer = Heap.withReactNavigationAutotrack(NavigationContainer);
    navigationContainer = (
      <HeapNavigationContainer {...props} ref={ref} linking={linking}>
        <RootStack />
      </HeapNavigationContainer>
    );
  }

  // @ts-ignore
  return (
    <ErrorBoundary FallbackComponent={ErrorBoundaryRender}>{navigationContainer}</ErrorBoundary>
  );
});

RootNavigator.displayName = "RootNavigator";

/**
 * A list of routes from which we're allowed to leave the app when
 * the user presses the back button on Android.
 *
 * Anything not on this list will be a standard `back` action in
 * react-navigation.
 *
 * `canExit` is used in ./app/app.tsx in the `useBackButtonHandler` hook.
 */
// TODO: do this
const exitRoutes = ["welcome"];
export const canExit = (routeName: string) => exitRoutes.includes(routeName);
